import { Suspense } from "react";
import { useSetAtom, useAtomValue } from "jotai";
import classNames from "classnames";
import { PressEvent } from "react-aria";
import { useCopilotDrawerState } from "@zapier/canvas-state";
import { Button, Colors } from "@zapier/design-system";
import {
  AssetType,
  draftFocusNodeIdAtom,
  shouldCopilotBeVisibleAtom,
  assetAccessAtomFamily_SUSPENDS,
  modalOriginPositionAtom,
  previewNodeIdAtom,
  sidebarAnimationAtom,
  isDraftOrTemplateNodeAtomFamily,
  isDraftNotTemplateNodeAtomFamily,
} from "@zapier/canvas-state";
import { FOCUS_MODE_ANIMATION } from "@zapier/canvas-constants";

import { ExpandIcon } from "../../../../icons/ExpandIcon";
import styles from "./AssetDetailsButton.module.css";

type Props = {
  id: string;
  assetType: AssetType;
  assetId: string;
  interfaceProjectId: string;
  hoveringOverAssetDetailsButton: boolean;
};

function SuspendedComponent({
  id,
  assetType,
  assetId,
  interfaceProjectId,
  hoveringOverAssetDetailsButton,
}: Props) {
  const isDraftOrTemplateNode = useAtomValue(
    isDraftOrTemplateNodeAtomFamily(id),
  );
  const isDraftNotTemplateNode = useAtomValue(
    isDraftNotTemplateNodeAtomFamily(id),
  );
  const setDraftFocusNodeId = useSetAtom(draftFocusNodeIdAtom);
  const setPreviewNodeId = useSetAtom(previewNodeIdAtom);
  const setModalOriginPosition = useSetAtom(modalOriginPositionAtom);
  const [isCopilotOpen, setIsCopilotOpen] = useCopilotDrawerState();
  const setSidebarAnimation = useSetAtom(sidebarAnimationAtom);
  const shouldCopilotBeVisible = useAtomValue(shouldCopilotBeVisibleAtom);

  // Helper function to calculate modal origin position from click event
  // It creates an effect that the modal opens from the node position
  const calculateModalOrigin = (
    e: PressEvent,
  ): { x: number; y: number } | null => {
    try {
      const target = e.target as HTMLElement;
      const buttonElement = target?.closest("button") || target;
      const nodeElement = buttonElement?.closest("[data-id]") as HTMLElement;

      if (!nodeElement) return null;

      const rect = nodeElement.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return null;

      return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
    } catch {
      return null; // Fallback for any DOM access issues
    }
  };

  // Helper function to handle sidebar animation sequence
  const animateSidebarToCopilot = () => {
    setSidebarAnimation("preparingContainer");

    setTimeout(() => {
      setIsCopilotOpen(true);
      setSidebarAnimation("fadeIn");

      setTimeout(() => {
        setSidebarAnimation(null);
      }, FOCUS_MODE_ANIMATION.SIDEBAR_FADE_DURATION);
    }, FOCUS_MODE_ANIMATION.CLOSE_BUFFER);
  };

  const openModal = (e: PressEvent) => {
    // Set transform origin for modal animation based on click position

    const originPosition = calculateModalOrigin(e);
    setModalOriginPosition(originPosition);

    // Animate sidebar to copilot if needed
    if (shouldCopilotBeVisible && !isCopilotOpen) {
      animateSidebarToCopilot();
    }

    if (isDraftOrTemplateNode) {
      setDraftFocusNodeId(id);
    } else {
      setPreviewNodeId(id);
    }
  };

  const { isAssetForbidden } = useAtomValue(
    assetAccessAtomFamily_SUSPENDS({
      assetId,
      interfaceProjectId,
      assetType,
    }),
  );

  if (isAssetForbidden) return null;

  const buttonText = `${isDraftOrTemplateNode ? "Preview" : "View"} asset`;

  return (
    <span
      className={classNames(styles.buttonWrapper, {
        [styles.hoveredInBuildMode]:
          !isDraftOrTemplateNode && hoveringOverAssetDetailsButton,
        [styles.hoveredInDraftOrTemplate]:
          isDraftOrTemplateNode && hoveringOverAssetDetailsButton,
        [styles.buttonWrapperReset]: assetType === "agent",
      })}
    >
      <Button
        size="medium"
        variant={isDraftNotTemplateNode ? "tertiary" : "primary"}
        onPress={openModal}
        iconPosition="start"
      >
        <ExpandIcon
          fill={isDraftNotTemplateNode ? Colors.TextDefault : undefined}
        />
        {hoveringOverAssetDetailsButton ? buttonText : null}
      </Button>
    </span>
  );
}

export function AssetDetailsButton({
  id,
  assetType,
  assetId,
  interfaceProjectId,
  hoveringOverAssetDetailsButton,
}: Props) {
  return (
    <Suspense fallback={null}>
      <SuspendedComponent
        id={id}
        assetType={assetType}
        assetId={assetId}
        interfaceProjectId={interfaceProjectId}
        hoveringOverAssetDetailsButton={hoveringOverAssetDetailsButton}
      />
    </Suspense>
  );
}
